<script setup lang="ts">
import { useAuthUser } from '@/helpers/globals'
import { computed, onBeforeMount, onMounted, ref, type Ref } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter, type LocationQueryValue } from 'vue-router'
import { instance as getApi } from '@/helpers/api'
import { errToStr } from '@/helpers/tools'
import type { Benutzer } from '@/models/Benutzer'
import { formRules } from '@/helpers/validators'
import { useToast } from '@/composables/toast'
import { useI18n } from 'vue-i18n'
import KpTextfield from '@/components/kp-textfield.vue'

const error: Ref<string | null> = ref(null)
const code: Ref<LocationQueryValue | null> = ref(null)
const pw1 = ref('')
const pw2 = ref('')
const loading = ref(false)
const benutzer: Ref<Benutzer | null> = ref(null)
const activationLoading = ref(false)
const newEmail: Ref<string | null> = ref(null)
const authUser = useAuthUser()
const route = useRoute()
const router = useRouter()

const email = computed(() => {
    return benutzer.value?.email || ''
})

const activateCodeInput = computed(() => {
    return loading.value === true || (code.value !== null && benutzer.value !== null)
})

const passwordsValid = computed(() => {
    return pw1.value.length > 0 && pw1.value === pw2.value
})

onBeforeMount(() => {
    let c = route.query.code
    if (Array.isArray(c)) {
        c = c[0]
    }
    code.value = c || null
})

onBeforeRouteUpdate((to) => {
    benutzer.value = authUser
    let c = to.query.code
    if (Array.isArray(c)) {
        c = c[0]
    }
    code.value = c || null
    if (code.value) {
        loadBenutzerInfo()
    }
})

onMounted(() => {
    if (code.value) {
        loadBenutzerInfo()
    }
})

async function loadBenutzerInfo() {
    error.value = null
    loading.value = true
    try {
        const res = await getApi().get('/auth/getActivationInfo', {
            params: {
                code: code.value,
            },
        })
        benutzer.value = res.data.benutzer
        newEmail.value = res.data.email
    } catch (e) {
        error.value = errToStr(e)
    } finally {
        loading.value = false
    }
}

async function onSubmit() {
    error.value = null
    activationLoading.value = true
    try {
        await getApi().post('/auth/activate', {
            email: benutzer.value?.email,
            code: code.value,
            password: pw1.value,
        })
        showLoginInfo()
        router.push({ name: 'login' })
    } catch (e) {
        error.value = errToStr(e)
    } finally {
        activationLoading.value = false
    }
}

const { t } = useI18n()
function showLoginInfo() {
    useToast().add({
        text: t('activation_successful', { email: email.value }),
        color: 'success',
        timeout: 12000,
    })
}
</script>

<template>
    <v-container>
        <v-card
            class="trans-bg"
            max-width="600px"
            style="margin: auto; margin-top: 10rem; padding-left: 1rem"
        >
            <v-slide-y-transition>
                <v-alert v-if="error" type="error" dense tile>{{ error }}</v-alert>
            </v-slide-y-transition>
            <v-card-title>{{ $t('activate_login') }}</v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="10">
                        <KpTextfield
                            v-model:model-value="code"
                            :disabled="activateCodeInput"
                            label="Aktivierungscode"
                        />
                    </v-col>
                    <v-col cols="2" align-self="end">
                        <v-btn
                            :disabled="activateCodeInput"
                            color="action white--text"
                            :loading="loading"
                            @click="loadBenutzerInfo"
                        >
                            {{ $t('check') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>

            <template v-if="code && benutzer">
                <v-card-title>
                    {{
                        newEmail
                            ? $t('email_confirm', { email: newEmail })
                            : $t('activation_of_mail', { email: email })
                    }}
                </v-card-title>
                <v-card-text v-if="!newEmail">
                    <p>{{ $t('please_define_pw') }}:</p>
                    <v-row>
                        <v-col>
                            <KpTextfield
                                v-model="email"
                                :label="$t('email')"
                                autocomplete="on"
                                iconType="email"
                                disabled
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <KpTextfield
                                v-model="pw1"
                                type="password"
                                :label="$t('pw_min_chars', { chars: 8 })"
                                iconType="password"
                                dense
                                :rules="[formRules.minLength(8)]"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <KpTextfield
                                v-model="pw2"
                                type="password"
                                :label="$t('repeat_pw')"
                                iconType="password"
                                dense
                                :error-messages="passwordsValid ? null : $t('pw_not_match')"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        v-if="newEmail"
                        color="primary"
                        :loading="activationLoading"
                        @click="$router.push({ name: 'login' })"
                    >
                        {{ $t('to_login') }}
                    </v-btn>
                    <v-btn
                        v-else
                        color="primary"
                        variant="flat"
                        :disabled="!passwordsValid || activationLoading"
                        :loading="activationLoading"
                        @click="onSubmit"
                    >
                        {{ $t('activate') }}
                    </v-btn>
                </v-card-actions>
            </template>
        </v-card>
    </v-container>
</template>
